import { Team } from "@/common/models/team";
import { useErrorStore } from "@/common/store/error.store";
import { defineStore } from "pinia";
import { Competition } from "@/common/models/competition";
import { Match, MatchResultRequest } from "@/common/models/match";
import {
  fetchCompetitionMatches,
  updateMatchResult,
  updateMatchSummaryVideoUrl,
} from "@/common/services/matches.service";
import { upsertEntities } from "@/common/store/utils";
import { useTeamsStore } from "@/common/store/teams.store";
import { useMatchStatsStore } from "./matchStats.store";

interface MatchesState {
  ids: string[];
  entities: { [id: string]: Match };
}

export const useMatchesStore = defineStore("matches", {
  state: (): MatchesState => ({
    ids: [],
    entities: {},
  }),

  getters: {
    all(): Match[] {
      const matchStatsStore = useMatchStatsStore();
      const teamsStore = useTeamsStore();
      return this.ids.map((id) => {
        const entity = this.entities[id];
        const teamIds = entity.teams.map((t) => t.team);
        const teams = teamIds.map((tId, index) => ({
          team: {
            ...teamsStore.entities[tId as string],
            id: tId,
          } as Team,
          kind: entity.teams[index].kind,
          result: entity.teams[index].result,
        }));
        return {
          ...entity,
          teams,
          stats: matchStatsStore.entities[id] || null,
        };
      });
    },

    allEntities(): { [id: string]: Match } {
      const matchStatsStore = useMatchStatsStore();
      const teamsStore = useTeamsStore();
      return this.ids.reduce((acc, id) => {
        const entity = this.entities[id];
        const teamIds = entity.teams.map((t) => t.team);
        const teams = teamIds.map((tId, index) => ({
          team: {
            ...teamsStore.entities[tId as string],
            id: tId,
          } as Team,
          kind: entity.teams[index].kind,
          result: entity.teams[index].result,
        }));
        acc[id] = {
          ...entity,
          teams,
          stats: matchStatsStore.entities[id] || null,
        };
        return acc;
      }, {} as { [id: string]: Match });
    },
  },

  actions: {
    async fetchCompetitionMatches(competition: Competition): Promise<void> {
      try {
        const matches = await fetchCompetitionMatches(competition);
        this.fetchCompetitionMatchesSuccess(matches);
      } catch (error) {
        this.fetchCompetitionMatchesError(error);
      }
    },

    fetchCompetitionMatchesSuccess(matches: Match[]): void {
      upsertEntities(matches, this);
    },

    fetchCompetitionMatchesError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`fetchCompetitionMatchesError: ${err.toString()}`);
    },

    async updateMatchResult(competition: Competition, match: Match, result: MatchResultRequest) {
      try {
        await updateMatchResult(competition, match, result);
      } catch (error) {
        this.updateMatchResultError(error);
      }
    },

    updateMatchResultError(err: any): void {
      const errorStore = useErrorStore();
      errorStore.throwError(`updateMatchResultError: ${err.toString()}`);
    },

    async updateMatchSummaryVideoUrl(
      competition: Competition,
      match: Match,
      summaryVideoUrl: string,
    ): Promise<void> {
      await updateMatchSummaryVideoUrl(competition, match, summaryVideoUrl);
      const entity = this.entities[match.id];
      if (entity) {
        entity.summaryVideoUrl = summaryVideoUrl;
      }
    },
  },
});
